import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import NavBar from "@/components/NavBar";
import ProductCard from "@/components/ProductCard";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/context/LanguageContext";
import { getProducts } from "@/lib/api";
import { AnimatedText } from "@/components/motion";

export default function Landing() {
  const [products, setProducts] = useState([]);
  const { lang } = useLang();

  useEffect(() => {
    getProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const L = {
    en: {
      kicker: "CRVD MOTORSPORT / ITALY",
      title1: "BUILT FOR TRACK.",
      title2: "NOT FOR SHOW.",
      sub: "Raw motorsport hardware for BMW M cars. Minimal. Honest. Pista-ready.",
      products: "PRODUCTS",
      scroll: "SCROLL",
    },
    it: {
      kicker: "CRVD MOTORSPORT / ITALIA",
      title1: "PER LA PISTA.",
      title2: "NON PER PARATA.",
      sub: "Hardware motorsport grezzo per BMW M. Minimale. Onesto. Pronto pista.",
      products: "PRODOTTI",
      scroll: "SCORRI",
    },
  }[lang];

  const scrollToProducts = () => {
    document.getElementById("products")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative bg-ink text-bone min-h-screen">
      <NavBar onBuy={scrollToProducts} />

      {/* Hero */}
      <section
        id="top"
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-24 pb-16 border-b border-[#222] overflow-hidden"
      >
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-5 md:px-10 w-full relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-8 h-px bg-wood" />
            <span className="font-mono text-[11px] tracking-tech text-wood">{L.kicker}</span>
          </motion.div>

          <h1 className="font-display tracking-brutal uppercase leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
            <span className="block">
              <AnimatedText text={L.title1} stagger={0.03} />
            </span>
            <span className="block text-wood mt-2">
              <AnimatedText text={L.title2} stagger={0.03} delay={0.4} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 max-w-xl font-sans text-base md:text-lg text-bone/70 leading-relaxed"
          >
            {L.sub}
          </motion.p>

          <motion.button
            onClick={scrollToProducts}
            data-testid="hero-scroll-cta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-12 inline-flex items-center gap-3 font-mono text-xs tracking-tech text-bone/70 hover:text-wood transition-colors group"
          >
            <span className="w-10 h-px bg-bone/40 group-hover:bg-wood transition-colors" />
            {L.scroll} ↓
          </motion.button>
        </div>
      </section>

      {/* Products Grid */}
      <section
        id="products"
        data-testid="products-section"
        className="relative py-24 md:py-32 border-b border-[#222]"
      >
        <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
          <div className="flex items-center gap-3 mb-12">
            <span className="w-8 h-px bg-wood" />
            <span className="font-mono text-[11px] tracking-tech text-wood">
              {L.products} · {products.length.toString().padStart(2, "0")}
            </span>
          </div>

          <div
            data-testid="products-grid"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {products.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
